import { apiFetch } from './client';
import { MediaType } from './media';
import type { SortField, SortOrder } from '../consts';
import { assertUseMediaAccount, assertUseMediaListResponse } from './guards';

export enum UseMediaAccountLinkStatus {
  Inactive = 0,
  Active = 1,
  TestMode = 2,
}

export enum JobNumberAcquisitionType {
  UnSelected = 0,
  Fixed = 1,
  Csv = 2,
  MediaExtract = 3,
  CsvWithBranchNumber = 4,
}

export enum RegisterChannelUse {
  UnSelected = 0,
  Use = 1,
  NotUse = 2,
}

export enum ExtractRuleCode {
  UseAsIs = 1,
  AfterHalfWidthSlash = 2,
  BeforeHalfWidthHyphen = 3,
  AfterFullWidthSlash = 4,
  AfterHalfWidthHyphen = 5,
  /** Valid only for extractBranchNumberRule — not offered for the plain extractRule selector. */
  LastOneDigit = 6,
  LastTwoDigits = 7,
  LastThreeDigits = 8,
}

export interface UseMediaAccount {
  useMediaAccountId: number;
  accountAlias: string;
  mediaId: number;
  mediaName: string;
  linkStatus: UseMediaAccountLinkStatus;
  jobNumberAcquisitionType: JobNumberAcquisitionType;
  registerChannelUse: RegisterChannelUse;
  /** Not returned by all endpoints — use selectedMediaType from Redux store instead. */
  mediaType?: MediaType;
  mailAddress: string;
  fixedJobNumber?: string | null;
  csvColumn?: string | null;
  extractRule?: ExtractRuleCode | null;
  extractBranchNumberRule?: ExtractRuleCode | null;
  registerChannelAlias?: string;
  loginCredentials?: string;
}

/** Subset of UseMediaAccount fields returned by the list endpoint. */
export interface UseMediaListItem {
  useMediaAccountId: number;
  accountAlias: string;
  mediaId: number;
  linkStatus: UseMediaAccountLinkStatus;
  mailAddress: string;
}

export interface UseMediaListResponse {
  data: UseMediaListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UseMediaSearchParams {
  keyword?: string;
  filter?: boolean;
  sort?: SortField;
  order?: SortOrder;
  page?: number;
  pageSize?: number;
}

/** Fields shared by both create and update request bodies. */
interface UseMediaRequestBase {
  accountAlias: string;
  mediaType: MediaType;
  jobNumberAcquisitionType: JobNumberAcquisitionType;
  registerChannelUse: RegisterChannelUse;
  registerChannelAlias: string;
  loginCredentials?: string;
  fixedJobNumber?: string;
  csvColumn?: string | null;
  extractRule?: ExtractRuleCode | null;
  extractBranchNumberRule?: ExtractRuleCode | null;
}

export interface UseMediaCreateRequest extends UseMediaRequestBase {
  mediaId: number | null;
}

export type UseMediaUpdateRequest = UseMediaRequestBase;

export async function getUseMediaList(
  params: UseMediaSearchParams = {},
): Promise<UseMediaListResponse> {
  const query = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)]),
    ),
  ).toString();
  const raw = await apiFetch<unknown>(`/api/v1/use-media${query ? `?${query}` : ''}`);
  assertUseMediaListResponse(raw);
  return raw;
}

export async function getUseMedia(id: number): Promise<UseMediaAccount> {
  const raw = await apiFetch<unknown>(`/api/v1/use-media/${id}`);
  assertUseMediaAccount(raw);
  return raw;
}

export async function createUseMedia(body: UseMediaCreateRequest): Promise<UseMediaAccount> {
  const raw = await apiFetch<unknown>('/api/v1/use-media', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  assertUseMediaAccount(raw);
  return raw;
}

export async function updateUseMedia(
  id: number,
  body: UseMediaUpdateRequest,
): Promise<UseMediaAccount> {
  const raw = await apiFetch<unknown>(`/api/v1/use-media/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  assertUseMediaAccount(raw);
  return raw;
}

export function patchUseMediaStatus(
  id: number,
  body: { newLinkStatus: UseMediaAccountLinkStatus },
): Promise<void> {
  return apiFetch<void>(`/api/v1/use-media/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteUseMedia(id: number): Promise<void> {
  return apiFetch<void>(`/api/v1/use-media/${id}`, { method: 'DELETE' });
}
